/**
 * Spend keys. The ONE place a key is derived, because R3 of the speculation
 * spec is that speculation and the real pick must produce byte-identical keys.
 *
 * R4: keys are derived only from SERVER-RETURNED identifiers — a starterSlug
 * or the gate's canonical. The single exception is gateKey, which is keyed on
 * normalized input text precisely because the gate is what turns text into a
 * stable identifier; it is also the cheapest call in the system (~$0.0004),
 * so a key collision there costs nothing.
 */

export function normalizeText(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function gateKey(text: string): string {
  return `gate:${normalizeText(text)}`;
}

/** Identity of a character draft. starterSlug wins when both are present. */
export function draftKey(source: { starterSlug?: string; canonical?: string }): string {
  const id = source.starterSlug ?? source.canonical;
  if (!id) throw new Error('draftKey needs a starterSlug or a gate canonical');
  return `draft:${normalizeText(id)}`;
}

export function commissionPreviewKey(characterId: string, steer = ''): string {
  return `preview:${characterId}:${normalizeText(steer)}`;
}

// There is deliberately no playbackKey. Playback URLs are ephemeral minted
// credentials with an expiry; once() never evicts on success, so memoizing
// them would hand back a dead URL an hour later. Only SPEND is memoized.

/**
 * The idempotency key for a commission.
 *
 * Derived from WHAT is being bought — this character, this request, this
 * length — rather than a random id, so a double tap, a refresh and a retried
 * fetch all produce the same key and therefore one hold. A random key would
 * be regenerated by the very re-render a double tap causes, which is exactly
 * when the guard has to work.
 *
 * Changing the length changes the key, because that is a different purchase.
 */
export function commissionKey(input: {
  characterId?: string;
  request: string;
  targetSeconds?: number;
}): string {
  return [
    'commission',
    // Prefixed rather than a bare sentinel: a bare 'no-cast' would collide
    // with a character whose id happened to be that string. Ids are UUIDs
    // today, so this is cheap insurance rather than a live bug — but the
    // consequence of the collision is one family's hold answering another
    // family's request, which is not a thing to leave to a format assumption.
    input.characterId ? `cast.${input.characterId}` : 'nocast',
    input.targetSeconds ?? 'default',
    normalizeText(input.request),
  ].join(':');
}
